<template>
  <div class="notification-provider">
    <NotificationCenter />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import NotificationCenter from './index.vue'

export default {
  name: 'NotificationProvider',
  components: {
    NotificationCenter
  },
  data() {
    return {
      lastRequestKey: null
    }
  },
  props: {
    userId: {
      type: [String, Number],
      default: null
    },
    wsBase: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapState('notification', {
      storeUserId: state => state.currentUserId,
      storeBaseUrl: state => state.websocketBaseUrl,
      storeConnected: state => state.websocketConnected
    })
  },
  watch: {
    userId: {
      immediate: true,
      handler(newVal) {
        this.ensureConnection(newVal, this.wsBase)
      }
    },
    wsBase(newVal) {
      this.ensureConnection(this.userId, newVal)
    },
    storeConnected(newVal) {
      if (!newVal) {
        this.lastRequestKey = null
      }
    }
  },
  created() {
    if (!this.wsBase && typeof window !== 'undefined') {
      window.__ensureNotificationConnection__ = (base) => {
        this.ensureConnection(this.userId, base || null)
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('notification:received', this.handleIncomingNotification)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined' && window.__ensureNotificationConnection__) {
      delete window.__ensureNotificationConnection__
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('notification:received', this.handleIncomingNotification)
    }
  },
  methods: {
    ensureConnection(userId, baseUrl) {
      if (!userId) {
        return
      }

      const resolvedBase = baseUrl || this.storeBaseUrl || undefined
      const requestKey = `${userId}__${resolvedBase || 'auto'}`

      if (!baseUrl && this.storeConnected && this.storeUserId === userId && (!resolvedBase || resolvedBase === this.storeBaseUrl)) {
        return
      }

      if (this.lastRequestKey === requestKey) {
        return
      }

      this.lastRequestKey = requestKey

      const payload = { userId }
      if (baseUrl) {
        payload.baseUrl = baseUrl
      }
      this.$store.dispatch('notification/initNotificationCenter', payload).catch((error) => {
        console.warn('全局通知中心初始化遇到问题，将静默重连:', error)
      })
    },

    openNotificationCenter(notificationId) {
      // 触发全局事件，通知中心组件会监听这个事件
      if (typeof window !== 'undefined') {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[NotificationProvider] 触发打开通知中心事件，ID:', notificationId)
        }
        const event = new CustomEvent('notification:open-center', {
          detail: { notificationId }
        })
        window.dispatchEvent(event)
      }
    },

    formatContent(content) {
      if (!content) return ''
      // 处理换行：将\n转换为<br>，将\r\n转换为<br>
      return content
        .replace(/\r\n/g, '<br>')
        .replace(/\n/g, '<br>')
        .replace(/\r/g, '<br>')
    },

    handleIncomingNotification(event) {
      const notification = event?.detail
      if (!notification) {
        return
      }

      const rawType = (notification.type || notification.notificationType || '').toString().toUpperCase()
      if (rawType === 'HEARTBEAT') {
        return
      }

      if (process.env.NODE_ENV !== 'production') {
        console.debug('[NotificationCenter] 接收到通知事件:', notification)
      }

      const payload = notification.payload || {}
      const titleCandidate = notification.title || payload.title || payload.flowName
      const title = typeof titleCandidate === 'string' ? titleCandidate.trim() : ''

      const contentCandidates = [
        notification.content,
        payload.content,
        payload.message,
        payload.description,
        payload.nodeName && payload.flowName ? `${payload.flowName} · ${payload.nodeName}` : null,
        payload.nodeName,
        payload.flowName
      ]
        .filter(Boolean)
        .map(item => (typeof item === 'string' ? item.trim() : item))

      const messageCandidate = contentCandidates.length > 0 ? contentCandidates[0] : ''
      const message = typeof messageCandidate === 'string' ? messageCandidate.trim() : ''

      if (!title && !message) {
        console.debug('[Notification] 忽略空通知卡片:', notification)
        return
      }

      const typeMap = {
        SUCCESS: 'success',
        INFO: 'info',
        WARNING: 'warning',
        WARN: 'warning',
        ERROR: 'error'
      }
      const elementType = typeMap[(notification.notificationType || payload.notificationType || '').toUpperCase()] || 'info'

      if (this.$notify) {
        // 打印完整的通知对象，查看所有字段
        // console.log('[NotificationProvider] 完整通知对象:', notification)
        // console.log('[NotificationProvider] 通知对象的所有键:', Object.keys(notification))
        
        // 获取通知ID，可能是id、notificationId、messageId或其他字段
        const notificationId = notification.id || notification.notificationId || notification.messageId || payload.id || payload.notificationId
        
        // 调试日志（生产环境可移除）
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[NotificationProvider] 完整通知对象:', notification)
          console.debug('[NotificationProvider] 提取的通知ID:', notificationId)
        }
        
        const notifyInstance = this.$notify({
          title,
          message: this.formatContent(message),
          type: elementType,
          position: 'top-right',
          duration: 10000,
          dangerouslyUseHTMLString: true,
          customClass: 'custom-notification show-notification',
          onClick: () => {
            // 点击通知卡片时，关闭弹窗并打开通知中心
            if (process.env.NODE_ENV !== 'production') {
              console.debug('[NotificationProvider] 点击通知卡片，ID:', notificationId)
            }
            if (notifyInstance && typeof notifyInstance.close === 'function') {
              notifyInstance.close()
            }
            // 直接触发全局事件，而不依赖 Vue 实例的上下文
            if (typeof window !== 'undefined' && notificationId) {
              const event = new CustomEvent('notification:open-center', {
                detail: { notificationId }
              })
              window.dispatchEvent(event)
            }
          },
          // 传递 notificationId 以便在 main.js 中也能访问
          _notificationId: notificationId
        })
      }
    }
  }
}
</script>

<style scoped>
.notification-provider {
  display: flex;
  align-items: center;
}
</style>

<style>
/* 全局样式，用于自定义通知卡片 */
.custom-notification {
  z-index: 10000 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 20px 0 rgba(54, 106, 255, 0.15) !important;
  border: 1px solid #366AFF !important;
  animation-duration: 0.5s !important;
  animation-fill-mode: both !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
}

.custom-notification:hover {
  box-shadow: 0 6px 30px 0 rgba(54, 106, 255, 0.25) !important;
  transform: translateY(-2px) !important;
}

.custom-notification .el-notification__icon {
  color: #366AFF !important;
  font-size: 20px !important;
}

.custom-notification .el-notification__title {
  color: #303133 !important;
  font-weight: 600 !important;
  font-size: 16px !important;
}

.custom-notification .el-notification__content {
  color: #606266 !important;
  font-size: 14px !important;
  text-align: left !important;
  display: -webkit-box !important;
  -webkit-line-clamp: 3 !important;
  line-clamp: 3 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  word-break: break-word !important;
  line-height: 1.5 !important;
  max-height: 4.5em !important;
  white-space: pre-wrap !important; /* 保持换行和空格 */
}

.custom-notification .el-notification__closeBtn {
  color: #909399 !important;
  font-size: 16px !important;
}

.custom-notification .el-notification__closeBtn:hover {
  color: #366AFF !important;
}

/* 调整通知的进入和离开动画时间 */
.custom-notification.right-enter-active {
  transition: all 0.5s ease !important;
}

.custom-notification.right-leave-active {
  transition: all 0.8s ease !important;
}

.custom-notification.right-enter {
  transform: translateX(100%) !important;
}

.custom-notification.right-leave-to {
  transform: translateX(100%) !important;
}

.el-notification:not(.show-notification) {
  display: none !important;
}
</style>
